// FeedbackForm.js (Public URL Component)
import { useParams } from "react-router-dom";
import { addDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../shared/services/firebase";
import React, { useState, useEffect } from "react";
import "./FeedbackForm.css";


export default function FeedbackForm() {
  const { uuid } = useParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState("");
  const [description, setDescription] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formType, setFormType] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchFormType() {
      setLoading(true);
      setErrorMsg("");
      try {
        const q = query(collection(db, "users"), where("uuid", "==", uuid));
        const snapshot = await getDocs(q);
        if (snapshot.empty) {
          setErrorMsg("Feedback form not found.");
          setFormType(null);
        } else {
          const userDoc = snapshot.docs[0].data();
          setFormType(userDoc.feedbackFormType || "simple-feedback");
        }
      } catch (err) {
        setErrorMsg("Failed to load feedback form.");
        setFormType(null);
      } finally {
        setLoading(false);
      }
    }
    fetchFormType();
  }, [uuid]);

  if (loading) {
    return <div className="feedback-form-container"><div>Loading form...</div></div>;
  }
  if (errorMsg) {
    return <div className="feedback-form-container"><div className="feedback-error-msg">{errorMsg}</div></div>;
  }

  // Render correct form type
  if (formType === "simple-feedback") {
    return <SimpleFeedbackForm uuid={uuid} />;
  }
  if (formType === "star-rating") {
    return <StarRatingForm uuid={uuid} />;
  }
  if (formType === "nps-survey") {
    return <NpsSurveyForm uuid={uuid} />;
  }
  if (formType === "emoji-feedback") {
    return <EmojiFeedbackForm uuid={uuid} />;
  }
  if (formType === "multiple-choice") {
    return <MultipleChoiceForm uuid={uuid} />;
  }
  if (formType === "thumbs-feedback") {
    return <ThumbsFeedbackForm uuid={uuid} />;
  }

  // fallback
  return (
    <div className="feedback-form-container">
      <h2 className="feedback-form-title">Feedback</h2>
      <p>Unknown form type.</p>
    </div>
  );
}

// --- Form Components ---
function SimpleFeedbackForm({ uuid }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");
    if (!email) {
      setErrorMsg("Email is required.");
      return;
    }
    if (!description) {
      setErrorMsg("Description is required.");
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, "feedback"), {
        uuid,
        name,
        email,
        description,
        createdAt: new Date(),
        type: "simple-feedback"
      });
      setName("");
      setEmail("");
      setDescription("");
      setSuccessMsg("Thank you for your feedback!");
    } catch (err) {
      setErrorMsg("Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="feedback-form-container">
      <h2 className="feedback-form-title">Simple Feedback</h2>
      {successMsg && <div className="feedback-success-msg">{successMsg}</div>}
      {errorMsg && <div className="feedback-error-msg">{errorMsg}</div>}
      <form className="feedback-form" onSubmit={handleSubmit}>
        <div className="feedback-form-group">
          <label htmlFor="name">Name (optional)</label>
          <input id="name" type="text" value={name} onChange={e => setName(e.target.value)} className="feedback-form-input" placeholder="Your name" />
        </div>
        <div className="feedback-form-group">
          <label htmlFor="email">Email <span style={{color: 'red'}}>*</span></label>
          <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="feedback-form-input" placeholder="you@email.com" required />
        </div>
        <div className="feedback-form-group">
          <label htmlFor="description">Description <span style={{color: 'red'}}>*</span></label>
          <textarea id="description" value={description} onChange={e => setDescription(e.target.value)} className="feedback-form-textarea" placeholder="Describe your feedback..." required />
        </div>
        <button className="feedback-form-submit" type="submit" disabled={submitting}>{submitting ? "Submitting..." : "Submit"}</button>
      </form>
    </div>
  );
}

function StarRatingForm({ uuid }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState("");
  const [comments, setComments] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");
    if (!email) {
      setErrorMsg("Email is required.");
      return;
    }
    if (!rating) {
      setErrorMsg("Star rating is required.");
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, "feedback"), {
        uuid,
        name,
        email,
        rating: Number(rating),
        comments,
        createdAt: new Date(),
        type: "star-rating"
      });
      setName("");
      setEmail("");
      setRating("");
      setComments("");
      setSuccessMsg("Thank you for your feedback!");
    } catch (err) {
      setErrorMsg("Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="feedback-form-container">
      <h2 className="feedback-form-title">Star Rating</h2>
      {successMsg && <div className="feedback-success-msg">{successMsg}</div>}
      {errorMsg && <div className="feedback-error-msg">{errorMsg}</div>}
      <form className="feedback-form" onSubmit={handleSubmit}>
        <div className="feedback-form-group">
          <label htmlFor="name">Name (optional)</label>
          <input id="name" type="text" value={name} onChange={e => setName(e.target.value)} className="feedback-form-input" placeholder="Your name" />
        </div>
        <div className="feedback-form-group">
          <label htmlFor="email">Email <span style={{color: 'red'}}>*</span></label>
          <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="feedback-form-input" placeholder="you@email.com" required />
        </div>
        <div className="feedback-form-group">
          <label>Star Rating <span style={{color: 'red'}}>*</span></label>
          <div className="feedback-form-rating-group">
            <span className="feedback-form-rating-label-text">Poor</span>
            {[...Array(5)].map((_, i) => (
              <label key={i+1} className="feedback-form-rating-label">
                <input type="radio" name="star-rating" value={i+1} checked={rating === String(i+1)} onChange={e => setRating(e.target.value)} className="feedback-form-rating-radio" required />
                {i+1}
              </label>
            ))}
            <span className="feedback-form-rating-label-text">Great</span>
          </div>
        </div>
        <div className="feedback-form-group">
          <label htmlFor="comments">Comments (optional)</label>
          <textarea id="comments" value={comments} onChange={e => setComments(e.target.value)} className="feedback-form-textarea" placeholder="Additional comments..." />
        </div>
        <button className="feedback-form-submit" type="submit" disabled={submitting}>{submitting ? "Submitting..." : "Submit"}</button>
      </form>
    </div>
  );
}

function NpsSurveyForm({ uuid }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [nps, setNps] = useState("");
  const [comments, setComments] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");
    if (!email) {
      setErrorMsg("Email is required.");
      return;
    }
    if (nps === "") {
      setErrorMsg("NPS score is required.");
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, "feedback"), {
        uuid,
        name,
        email,
        nps: Number(nps),
        comments,
        createdAt: new Date(),
        type: "nps-survey"
      });
      setName("");
      setEmail("");
      setNps("");
      setComments("");
      setSuccessMsg("Thank you for your feedback!");
    } catch (err) {
      setErrorMsg("Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="feedback-form-container">
      <h2 className="feedback-form-title">NPS Survey</h2>
      {successMsg && <div className="feedback-success-msg">{successMsg}</div>}
      {errorMsg && <div className="feedback-error-msg">{errorMsg}</div>}
      <form className="feedback-form" onSubmit={handleSubmit}>
        <div className="feedback-form-group">
          <label htmlFor="name">Name (optional)</label>
          <input id="name" type="text" value={name} onChange={e => setName(e.target.value)} className="feedback-form-input" placeholder="Your name" />
        </div>
        <div className="feedback-form-group">
          <label htmlFor="email">Email <span style={{color: 'red'}}>*</span></label>
          <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="feedback-form-input" placeholder="you@email.com" required />
        </div>
        <div className="feedback-form-group">
          <label>NPS Score (0-10) <span style={{color: 'red'}}>*</span></label>
          <div className="feedback-form-rating-group">
            {[...Array(11)].map((_, i) => (
              <label key={i} className="feedback-form-rating-label">
                <input type="radio" name="nps-score" value={i} checked={nps === String(i)} onChange={e => setNps(e.target.value)} className="feedback-form-rating-radio" required />
                {i}
              </label>
            ))}
          </div>
        </div>
        <div className="feedback-form-group">
          <label htmlFor="comments">Comments (optional)</label>
          <textarea id="comments" value={comments} onChange={e => setComments(e.target.value)} className="feedback-form-textarea" placeholder="Additional comments..." />
        </div>
        <button className="feedback-form-submit" type="submit" disabled={submitting}>{submitting ? "Submitting..." : "Submit"}</button>
      </form>
    </div>
  );
}

function EmojiFeedbackForm({ uuid }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [emoji, setEmoji] = useState("");
  const [comments, setComments] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const emojis = ["😀", "🙂", "😐", "🙁", "😡"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");
    if (!email) {
      setErrorMsg("Email is required.");
      return;
    }
    if (!emoji) {
      setErrorMsg("Please select an emoji.");
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, "feedback"), {
        uuid,
        name,
        email,
        emoji,
        comments,
        createdAt: new Date(),
        type: "emoji-feedback"
      });
      setName("");
      setEmail("");
      setEmoji("");
      setComments("");
      setSuccessMsg("Thank you for your feedback!");
    } catch (err) {
      setErrorMsg("Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="feedback-form-container">
      <h2 className="feedback-form-title">Emoji Feedback</h2>
      {successMsg && <div className="feedback-success-msg">{successMsg}</div>}
      {errorMsg && <div className="feedback-error-msg">{errorMsg}</div>}
      <form className="feedback-form" onSubmit={handleSubmit}>
        <div className="feedback-form-group">
          <label htmlFor="name">Name (optional)</label>
          <input id="name" type="text" value={name} onChange={e => setName(e.target.value)} className="feedback-form-input" placeholder="Your name" />
        </div>
        <div className="feedback-form-group">
          <label htmlFor="email">Email <span style={{color: 'red'}}>*</span></label>
          <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="feedback-form-input" placeholder="you@email.com" required />
        </div>
        <div className="feedback-form-group">
          <label>How do you feel? <span style={{color: 'red'}}>*</span></label>
          <div className="feedback-form-rating-group">
            {emojis.map((em, i) => (
              <label key={em} className="feedback-form-rating-label">
                <input type="radio" name="emoji" value={em} checked={emoji === em} onChange={e => setEmoji(e.target.value)} className="feedback-form-rating-radio" required />
                <span style={{fontSize: "1.5rem"}}>{em}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="feedback-form-group">
          <label htmlFor="comments">Comments (optional)</label>
          <textarea id="comments" value={comments} onChange={e => setComments(e.target.value)} className="feedback-form-textarea" placeholder="Additional comments..." />
        </div>
        <button className="feedback-form-submit" type="submit" disabled={submitting}>{submitting ? "Submitting..." : "Submit"}</button>
      </form>
    </div>
  );
}

function MultipleChoiceForm({ uuid }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [choice, setChoice] = useState("");
  const [comments, setComments] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const options = ["Social Media", "Friend/Colleague", "Search Engine", "Advertisement", "Other"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");
    if (!email) {
      setErrorMsg("Email is required.");
      return;
    }
    if (!choice) {
      setErrorMsg("Please select an option.");
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, "feedback"), {
        uuid,
        name,
        email,
        choice,
        comments,
        createdAt: new Date(),
        type: "multiple-choice"
      });
      setName("");
      setEmail("");
      setChoice("");
      setComments("");
      setSuccessMsg("Thank you for your feedback!");
    } catch (err) {
      setErrorMsg("Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="feedback-form-container">
      <h2 className="feedback-form-title">How did you hear about us?</h2>
      {successMsg && <div className="feedback-success-msg">{successMsg}</div>}
      {errorMsg && <div className="feedback-error-msg">{errorMsg}</div>}
      <form className="feedback-form" onSubmit={handleSubmit}>
        <div className="feedback-form-group">
          <label htmlFor="name">Name (optional)</label>
          <input id="name" type="text" value={name} onChange={e => setName(e.target.value)} className="feedback-form-input" placeholder="Your name" />
        </div>
        <div className="feedback-form-group">
          <label htmlFor="email">Email <span style={{color: 'red'}}>*</span></label>
          <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="feedback-form-input" placeholder="you@email.com" required />
        </div>
        <div className="feedback-form-group">
          <label>How did you hear about us? <span style={{color: 'red'}}>*</span></label>
          <div className="feedback-form-rating-group">
            {options.map(opt => (
              <label key={opt} className="feedback-form-rating-label">
                <input type="radio" name="mcq" value={opt} checked={choice === opt} onChange={e => setChoice(e.target.value)} className="feedback-form-rating-radio" required />
                {opt}
              </label>
            ))}
          </div>
        </div>
        <div className="feedback-form-group">
          <label htmlFor="comments">Comments (optional)</label>
          <textarea id="comments" value={comments} onChange={e => setComments(e.target.value)} className="feedback-form-textarea" placeholder="Additional comments..." />
        </div>
        <button className="feedback-form-submit" type="submit" disabled={submitting}>{submitting ? "Submitting..." : "Submit"}</button>
      </form>
    </div>
  );
}

function ThumbsFeedbackForm({ uuid }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [thumb, setThumb] = useState("");
  const [comments, setComments] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const options = [
    { label: "Thumbs Up", value: "up", icon: "👍" },
    { label: "Thumbs Down", value: "down", icon: "👎" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");
    if (!email) {
      setErrorMsg("Email is required.");
      return;
    }
    if (!thumb) {
      setErrorMsg("Please select thumbs up or down.");
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, "feedback"), {
        uuid,
        name,
        email,
        thumb,
        comments,
        createdAt: new Date(),
        type: "thumbs-feedback"
      });
      setName("");
      setEmail("");
      setThumb("");
      setComments("");
      setSuccessMsg("Thank you for your feedback!");
    } catch (err) {
      setErrorMsg("Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="feedback-form-container">
      <h2 className="feedback-form-title">Thumbs Up/Down</h2>
      {successMsg && <div className="feedback-success-msg">{successMsg}</div>}
      {errorMsg && <div className="feedback-error-msg">{errorMsg}</div>}
      <form className="feedback-form" onSubmit={handleSubmit}>
        <div className="feedback-form-group">
          <label htmlFor="name">Name (optional)</label>
          <input id="name" type="text" value={name} onChange={e => setName(e.target.value)} className="feedback-form-input" placeholder="Your name" />
        </div>
        <div className="feedback-form-group">
          <label htmlFor="email">Email <span style={{color: 'red'}}>*</span></label>
          <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="feedback-form-input" placeholder="you@email.com" required />
        </div>
        <div className="feedback-form-group">
          <label>Thumbs <span style={{color: 'red'}}>*</span></label>
          <div className="feedback-form-rating-group">
            {options.map(opt => (
              <label key={opt.value} className="feedback-form-rating-label">
                <input type="radio" name="thumb" value={opt.value} checked={thumb === opt.value} onChange={e => setThumb(e.target.value)} className="feedback-form-rating-radio" required />
                <span style={{fontSize: "1.5rem"}}>{opt.icon}</span> {opt.label}
              </label>
            ))}
          </div>
        </div>
        <div className="feedback-form-group">
          <label htmlFor="comments">Comments (optional)</label>
          <textarea id="comments" value={comments} onChange={e => setComments(e.target.value)} className="feedback-form-textarea" placeholder="Additional comments..." />
        </div>
        <button className="feedback-form-submit" type="submit" disabled={submitting}>{submitting ? "Submitting..." : "Submit"}</button>
      </form>
    </div>
  );
}
